import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AreaCriteria, { fromParams, toParams } from '../components/area/AreaCriteria';
import type { Criteria } from '../components/area/AreaCriteria';
import AreaReadingPanel, { IN_PROGRESS } from '../components/area/AreaReadingPanel';
import AreaResults from '../components/area/AreaResults';
import ErrorPanel from '../components/ErrorPanel';
import Notice from '../components/Notice';
import Spinner from '../components/Spinner';
import { tx } from '../i18n';
import { api } from '../lib/api';
import type { AreaPlacesResponse, AreaResponse, AreaSchool } from '../types/api';

/** How often to ask again while an area is being read. */
const POLL_MS = 4000;

/** True when two sets of criteria would produce the same search. */
function same(a: Criteria, b: Criteria): boolean {
  return toParams(a).toString() === toParams(b).toString();
}

/**
 * Search by criteria.
 *
 * <p>The form is a draft until Search is pressed; the URL holds what was actually searched for, so
 * a result is a link somebody can send and the back button walks through real searches rather
 * than through keystrokes. While the draft has moved away from the result on screen, the form
 * says so - a stale answer that looks current is worse than no answer.
 */
export default function AreaSearchPage() {
  const [params, setParams] = useSearchParams();
  const searched = fromParams(params);

  const [draft, setDraft] = useState<Criteria>(searched);
  const [places, setPlaces] = useState<AreaPlacesResponse | null>(null);
  const [area, setArea] = useState<AreaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .areaPlaces(controller.signal)
      .then(setPlaces)
      .catch(() => setPlaces(null));
    return () => controller.abort();
  }, []);

  const key = params.toString();
  // A counter rather than a timestamp: it is what re-runs the fetch, and it only ever advances
  // when the last answer said there was still work happening.
  const [tick, setTick] = useState(0);
  // True while the re-fetch is a poll rather than a new search, which is what keeps the button
  // and the spinner still: an area that redraws every four seconds is not a loading state.
  const polling = useRef(false);

  // A link somebody was sent, or the back button: the form follows what is being shown, and a
  // poll belonging to the last search is not carried into this one.
  useEffect(() => {
    setDraft(fromParams(new URLSearchParams(key)));
    polling.current = false;
  }, [key]);

  useEffect(() => {
    const current = fromParams(new URLSearchParams(key));
    if (current.outcodes.length === 0) {
      setArea(null);
      return undefined;
    }
    const controller = new AbortController();
    const silent = polling.current;
    let timer = 0;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    api
      .areas(
        current.outcodes,
        {
          minBand: current.minBand,
          phase: current.phase,
          schoolMetres: current.schoolMetres,
          goodOnly: current.goodOnly,
          stationMetres: current.stationMetres,
          busMetres: current.busMetres,
        },
        controller.signal,
      )
      .then((next) => {
        setArea(next);
        // A poll that works again clears a message about one that did not.
        setError(null);
        // Ask again only while something is still being read; a finished answer stops the loop.
        if (next.reading?.some((row) => IN_PROGRESS.includes(row.state))) {
          polling.current = true;
          timer = window.setTimeout(() => setTick((value) => value + 1), POLL_MS);
        } else {
          polling.current = false;
        }
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(cause);
        // A reading in progress outlives a failed poll. The register is still being read in the
        // background whatever this one request did, so the screen keeps asking rather than
        // freezing at the last answer and looking stuck for the rest of the job.
        if (polling.current) {
          timer = window.setTimeout(() => setTick((value) => value + 1), POLL_MS);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted && !silent) {
          setLoading(false);
        }
      });
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [key, tick]);

  const run = useCallback(() => {
    if (draft.outcodes.length === 0) {
      return;
    }
    setParams(toParams(draft));
  }, [draft, setParams]);

  const wanted = useCallback(
    (school: AreaSchool) => {
      if (searched.phase && school.phase !== searched.phase && school.phase !== 'ALL_THROUGH') {
        return false;
      }
      return !searched.goodOnly || school.judgement === 'Good' || school.judgement === 'Outstanding';
    },
    [searched.phase, searched.goodOnly],
  );

  return (
    <div className="page stack">
      <div>
        <h1 className="card__title">
          {tx('Find an area', 'Bölge bulun', 'Znajdź obszar', 'Encontrar uma zona', 'Encontrar una zona')}
        </h1>
        <p className="small muted">
          {tx(
            'Set the criteria and the map follows. Everything here is counted from certificates and registers that exist; nothing is estimated between them.',
            'Kriterleri belirleyin, harita ona göre çizilir. Buradaki her şey var olan sertifika ve kayıtlardan sayılır; aralar tahmin edilmez.',
            'Ustaw kryteria, a mapa za nimi podąży. Wszystko tutaj jest policzone z istniejących świadectw i rejestrów; nic nie jest szacowane pomiędzy nimi.',
            'Defina os critérios e o mapa segue. Tudo aqui é contado a partir de certificados e registos existentes; nada é estimado entre eles.',
            'Marca los criterios y el mapa los sigue. Todo aquí se cuenta de certificados y registros que existen; nada se estima entre ellos.',
          )}
        </p>
      </div>

      <AreaCriteria
        value={draft}
        places={places}
        onChange={setDraft}
        onSearch={run}
        dirty={area !== null && !same(draft, searched)}
        loading={loading}
      />

      {error ? <ErrorPanel cause={error} /> : null}

      {searched.outcodes.length === 0 ? (
        <Notice
          tone="info"
          title={tx('Choose an area to begin', 'Başlamak için bir bölge seçin', 'Wybierz obszar, aby zacząć', 'Escolha uma área para começar', 'Elige una zona para empezar')}
        >
          {tx(
            'Every other criterion is optional; this one is not, because there is no such thing as the energy stock of nowhere in particular.',
            'Diğer tüm kriterler isteğe bağlı; bu değil — belirli bir yer olmadan konut stoku diye bir şey yok.',
            'Każde inne kryterium jest opcjonalne; to nie, bo nie istnieje zasób mieszkaniowy nigdzie w szczególności.',
            'Todos os outros critérios são opcionais; este não, porque não existe o parque habitacional de lugar nenhum.',
            'Todos los demás criterios son opcionales; este no, porque no existe el parque de viviendas de ningún sitio en concreto.',
          )}
        </Notice>
      ) : null}

      {area ? <AreaReadingPanel reading={area.reading ?? []} /> : null}

      {loading && !area ? (
        <div className="card loading-panel">
          <Spinner label={tx('Drawing the area', 'Bölge çiziliyor', 'Rysowanie obszaru', 'A desenhar a área', 'Dibujando la zona')} />
        </div>
      ) : null}

      {area ? <AreaResults area={area} isWanted={wanted} /> : null}
    </div>
  );
}
